import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiChevronDown } from '@mdi/js';
import { CircuitNode, ComponentType, CoolantFluid } from '../types';
import { apiService } from '../services/apiService';
import { CollapsibleSection } from './CollapsibleSection';
import { ComponentPickerModal } from './ComponentPickerModal';
import { useUnits, Dimension, convertForDisplay, convertToMetric } from '../units';
import { ALWAYS_SHOWN, DEFAULT_FIXED_FLOW_LPM, pumpMode } from '../services/componentDefaults';

const PanelContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px;
`;

const PropertyGroup = styled.div`
  margin-bottom: 12px;
`;

const PropertyLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #5f6368;
  margin-bottom: 3px;
`;

const PropertyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PropertyCheckbox = styled.input`
  margin: 0;
  flex-shrink: 0;
`;

const PropertyInputContainer = styled.div`
  flex: 1;
`;

const PropertyInput = styled.input`
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-size: 13px;
  color: #202124;
  background-color: #fff;

  &:focus {
    outline: none;
    border-color: #1a73e8;
    box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
  }

  &:disabled {
    background-color: #f8f9fa;
    color: #5f6368;
    cursor: not-allowed;
  }
`;

const PropertyValue = styled.div`
  font-size: 13px;
  color: #202124;
  padding: 6px 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e8eaed;
`;

const ModelSelectButton = styled.button`
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-size: 13px;
  color: #202124;
  background: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  font-family: inherit;

  &:hover {
    border-color: #1a73e8;
  }

  &:focus {
    outline: none;
    border-color: #1a73e8;
    box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
  }
`;

const ModelDescription = styled.div`
  font-size: 11px;
  color: #5f6368;
  margin-top: 4px;
  padding: 4px 8px;
  background: #f8f9fa;
  border-radius: 3px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
`;

const ToggleLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #202124;
  cursor: pointer;
  user-select: none;
`;

const ReadOnlyHint = styled.div`
  font-size: 11px;
  color: #9aa0a6;
  font-style: italic;
  margin-bottom: 8px;
`;

const PropertySelect = styled.select`
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #dadce0;
  border-radius: 4px;
  font-size: 13px;
  color: #202124;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #1a73e8;
    box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
  }
`;

const PROPERTY_META: Record<string, { label: string; unit: string; dimension?: Dimension }> = {
  // Pump
  flow_rate: { label: 'Flow Rate', unit: 'l/min', dimension: 'flow' },
  // Pipe
  inner_diameter_mm: { label: 'Inner Diameter', unit: 'mm', dimension: 'length_mm' },
  length_m: { label: 'Length', unit: 'm', dimension: 'length_m' },
  // Heat source
  heat_input_kw: { label: 'Heat Input', unit: 'kW', dimension: 'power' },
  // Restriction
  k_factor: { label: 'Loss Coefficient', unit: 'K' },
  // Custom component
  pressure_drop_mbar: { label: 'Pressure Drop', unit: 'mbar', dimension: 'pressure' },
  reference_flow_lpm: { label: 'at Flow Rate', unit: 'l/min', dimension: 'flow' },
  heat_rejection_kw: { label: 'Heat Rejection', unit: 'kW', dimension: 'power' },
  // Fixed pump
  fixed_flow_lpm: { label: 'Fixed Flow Rate', unit: 'l/min', dimension: 'flow' },
};

// Properties that should not be shown in the editable properties section
const HIDDEN_PROPERTIES = ['custom_curve', 'fixed_flow_lpm', 'fluid_id'];


interface PropertiesPanelProps {
  node: CircuitNode;
  onNodeUpdate: (nodeId: string, updates: Partial<CircuitNode['data']>) => void;
  availableModels: ComponentType[];
  openPickerOnMount?: boolean;
  onPickerOpened?: () => void;
  readOnly?: boolean;
  /** Coolant library, for the pump's coolant choice. */
  fluids?: CoolantFluid[];
  /** The circuit this component belongs to, if it is connected. */
  circuitInfo?: { index: number; total: number; size: number } | null;
  /** Set the coolant of the whole circuit this pump drives. */
  onCircuitCoolantChange?: (nodeId: string, fluidId: number) => void;
  /** Admins may add a custom component to the shared library. */
  canAddToLibrary?: boolean;
  onAddToLibrary?: (nodeId: string, meta: LibraryMeta) => Promise<void>;
}

export interface LibraryMeta {
  name: string;
  manufacturer?: string;
  description?: string;
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  node,
  onNodeUpdate,
  availableModels,
  openPickerOnMount,
  onPickerOpened,
  readOnly: readOnlyProp,
  fluids = [],
  circuitInfo,
  onCircuitCoolantChange,
  canAddToLibrary,
  onAddToLibrary,
}) => {
  const { component, custom_name, properties, display_properties = {}, max_temperatures } = node.data;
  const [showPicker, setShowPicker] = useState(false);
  const [libraryForm, setLibraryForm] = useState<LibraryMeta | null>(null);
  const [libraryBusy, setLibraryBusy] = useState(false);
  const [libraryMessage, setLibraryMessage] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    setLibraryForm(null);
    setLibraryMessage(null);
  }, [node.id]);
  const units = useUnits();

  const isModelBased = component.id > 0;
  const isPump = component.component_type === 'pump';
  const isFixedFlow = pumpMode(properties) === 'fixed';
  const alwaysShown = ALWAYS_SHOWN[component.component_type] || [];

  const handlePropertyChange = (key: string, value: any) => {
    onNodeUpdate(node.id, {
      properties: { ...properties, [key]: value },
    });
  };

  const handleDisplayPropertyChange = (key: string, display: boolean) => {
    onNodeUpdate(node.id, {
      display_properties: { ...display_properties, [key]: display },
    });
  };

  const handleMaxTempChange = (field: 'inlet' | 'outlet', value: string) => {
    const numValue = value === '' ? undefined : parseFloat(value);
    onNodeUpdate(node.id, {
      max_temperatures: {
        ...max_temperatures,
        [field]: isNaN(numValue as number) ? undefined : numValue,
      },
    });
  };

  const handleFixedFlowToggle = (enabled: boolean) => {
    const newProps = { ...properties };
    delete newProps.flow_rate; // superseded by the two pump modes
    if (enabled) {
      newProps.fixed_flow_lpm = newProps.fixed_flow_lpm ?? DEFAULT_FIXED_FLOW_LPM;
    } else {
      delete newProps.fixed_flow_lpm;
    }
    onNodeUpdate(node.id, { properties: newProps });
  };

  const hasCustomCurve = Array.isArray(properties?.custom_curve) && properties.custom_curve.length > 0;
  const curveSource = isModelBased
    ? `Curve of model ${component.name}`
    : hasCustomCurve
      ? `Custom curve (${properties.custom_curve.length} points)`
      : null;

  const handleFixedFlowValueChange = (value: number) => {
    onNodeUpdate(node.id, {
      properties: { ...properties, fixed_flow_lpm: value },
    });
  };

  const handleModelSelect = async (model: ComponentType | null, customProperties?: Record<string, any>) => {
    setShowPicker(false);

    if (!model) {
      // Revert to generic
      const genericName = component.component_type_display;
      const updates: Partial<CircuitNode['data']> = {
        component: {
          ...component,
          id: 0,
          name: genericName,
          description: '',
          curves: undefined,
        },
      };
      if (!custom_name || custom_name === component.name) {
        updates.custom_name = '';
      }
      onNodeUpdate(node.id, updates);
      return;
    }

    // Custom component (id === 0 with customProperties)
    if (model.id === 0 && customProperties) {
      const updates: Partial<CircuitNode['data']> = {
        component: model,
        properties: customProperties,
      };
      if (!custom_name || custom_name === component.name) {
        updates.custom_name = model.name;
      }
      onNodeUpdate(node.id, updates);
      return;
    }

    const updates: Partial<CircuitNode['data']> = {
      component: model,
      properties: { ...model.default_properties },
    };
    if (!custom_name || custom_name === component.name) {
      updates.custom_name = model.name;
    }
    if (model.max_inlet_temperature != null || model.max_outlet_temperature != null) {
      updates.max_temperatures = {
        ...max_temperatures,
        ...(model.max_inlet_temperature != null ? { inlet: model.max_inlet_temperature } : {}),
        ...(model.max_outlet_temperature != null ? { outlet: model.max_outlet_temperature } : {}),
      };
    }
    onNodeUpdate(node.id, updates);

    // Fetch full component detail (with curves) in background
    if (model.id > 0) {
      try {
        const fullComponent = await apiService.getComponent(model.id);
        onNodeUpdate(node.id, { component: fullComponent });
      } catch {
        // Curves are non-critical
      }
    }
  };

  // Auto-open picker when requested (e.g. from double-click)
  const pickerOpenedRef = useRef(false);
  useEffect(() => {
    if (openPickerOnMount && !pickerOpenedRef.current) {
      pickerOpenedRef.current = true;
      setShowPicker(true);
      onPickerOpened?.();
    }
    if (!openPickerOnMount) {
      pickerOpenedRef.current = false;
    }
  }, [openPickerOnMount, onPickerOpened]);

  const renderPropertyInput = (key: string, value: any, readOnly: boolean) => {
    if (typeof value === 'number') {
      const meta = PROPERTY_META[key];
      const dim = meta?.dimension;
      const displayValue = dim ? convertForDisplay(value, dim, units.system) : value;
      if (readOnly) {
        const displayUnit = meta ? (meta.dimension ? units.unit(meta.dimension) : meta.unit) : '';
        return <PropertyValue>{parseFloat(displayValue.toFixed(4))} {displayUnit}</PropertyValue>;
      }
      return (
        <PropertyInput
          type="number"
          value={parseFloat(displayValue.toFixed(4))}
          step="any"
          onChange={(e) => {
            const raw = parseFloat(e.target.value) || 0;
            const stored = dim ? convertToMetric(raw, dim, units.system) : raw;
            handlePropertyChange(key, stored);
          }}
        />
      );
    }
    if (typeof value === 'boolean') {
      return (
        <PropertyInput
          type="checkbox"
          checked={value}
          disabled={readOnly}
          onChange={(e) => handlePropertyChange(key, e.target.checked)}
          style={{ width: 'auto' }}
        />
      );
    }
    if (typeof value === 'object') {
      return <PropertyValue>{JSON.stringify(value)}</PropertyValue>;
    }
    if (readOnly) {
      return <PropertyValue>{value || '-'}</PropertyValue>;
    }
    return (
      <PropertyInput
        type="text"
        value={value || ''}
        onChange={(e) => handlePropertyChange(key, e.target.value)}
      />
    );
  };

  const getPropertyLabel = (key: string) => {
    const meta = PROPERTY_META[key];
    if (meta) {
      const displayUnit = meta.dimension ? units.unit(meta.dimension) : meta.unit;
      return `${meta.label} [${displayUnit}]`;
    }
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const selectedModel = availableModels.find(m => m.id === component.id);

  // Filter out hidden properties (like custom_curve arrays, fixed_flow_lpm handled separately)
  // A pump's flow is set by its mode (curve or fixed flow), never by a free field.
  const visibleProperties = properties
    ? Object.entries(properties).filter(([key]) =>
        !HIDDEN_PROPERTIES.includes(key) && !(isPump && key === 'flow_rate'))
    : [];

  return (
    <PanelContainer>
      <CollapsibleSection title="General">
        <PropertyGroup>
          <PropertyLabel>Name</PropertyLabel>
          <PropertyInput
            value={custom_name || ''}
            placeholder={component.name}
            onChange={(e) => onNodeUpdate(node.id, { custom_name: e.target.value })}
            disabled={readOnlyProp}
          />
        </PropertyGroup>
      </CollapsibleSection>

      <CollapsibleSection title="Model">
        <PropertyGroup>
          <PropertyLabel>Select Model</PropertyLabel>
          <ModelSelectButton onClick={() => !readOnlyProp && setShowPicker(true)} style={readOnlyProp ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}>
            <span>
              {selectedModel
                ? selectedModel.name
                : component.description?.startsWith('Custom:')
                  ? `Custom ${component.component_type_display.toLowerCase()}`
                  : `Generic ${component.component_type_display}`}
            </span>
            <Icon path={mdiChevronDown} size={0.5} color="#5f6368" />
          </ModelSelectButton>
          {selectedModel?.description && (
            <ModelDescription>{selectedModel.description}</ModelDescription>
          )}
        </PropertyGroup>
      </CollapsibleSection>

      {visibleProperties.length > 0 && (
        <CollapsibleSection title="Properties">
          {isModelBased && (
            <ReadOnlyHint>Parameters are fixed for this model. Use Generic to edit.</ReadOnlyHint>
          )}
          {visibleProperties.map(([key, value]) => (
            <PropertyGroup key={key}>
              <PropertyLabel>{getPropertyLabel(key)}</PropertyLabel>
              <PropertyRow>
                {alwaysShown.includes(key) ? (
                  <PropertyCheckbox type="checkbox" checked disabled title="Always shown on the node" />
                ) : (
                  <PropertyCheckbox
                    type="checkbox"
                    checked={!!display_properties[key]}
                    onChange={(e) => handleDisplayPropertyChange(key, e.target.checked)}
                    title="Show on node"
                    disabled={readOnlyProp}
                  />
                )}
                <PropertyInputContainer>
                  {renderPropertyInput(key, value, readOnlyProp || isModelBased)}
                </PropertyInputContainer>
              </PropertyRow>
            </PropertyGroup>
          ))}
        </CollapsibleSection>
      )}

      {isPump && (
        <CollapsibleSection title="Coolant">
          <PropertyGroup>
            <PropertyLabel htmlFor="pump-coolant">Coolant in this circuit</PropertyLabel>
            <PropertySelect
              id="pump-coolant"
              value={properties?.fluid_id ?? ''}
              disabled={readOnlyProp || fluids.length === 0}
              onChange={(e) => {
                const id = Number(e.target.value);
                if (!id) return;
                if (onCircuitCoolantChange) onCircuitCoolantChange(node.id, id);
                else handlePropertyChange('fluid_id', id);
              }}
            >
              {!properties?.fluid_id && <option value="">Choose a coolant…</option>}
              {fluids.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </PropertySelect>
            <ReadOnlyHint>
              {circuitInfo && circuitInfo.total > 1
                ? `Applies to circuit ${circuitInfo.index} of ${circuitInfo.total} (${circuitInfo.size} components) and every pump in it. Other circuits keep their own coolant.`
                : 'Applies to the whole circuit this pump drives.'}
            </ReadOnlyHint>
          </PropertyGroup>
        </CollapsibleSection>
      )}

      {isPump && (
        <CollapsibleSection title="Pump Mode">
          <ToggleRow>
            <PropertyCheckbox
              type="radio"
              name={`pump-mode-${node.id}`}
              id="pump-mode-curve"
              checked={!isFixedFlow}
              disabled={readOnlyProp}
              onChange={() => handleFixedFlowToggle(false)}
            />
            <ToggleLabel htmlFor="pump-mode-curve">Pump curve</ToggleLabel>
          </ToggleRow>
          <ToggleRow>
            <PropertyCheckbox
              type="radio"
              name={`pump-mode-${node.id}`}
              id="fixed-flow-toggle"
              checked={isFixedFlow}
              disabled={readOnlyProp}
              onChange={() => handleFixedFlowToggle(true)}
            />
            <ToggleLabel htmlFor="fixed-flow-toggle">Fixed flow</ToggleLabel>
          </ToggleRow>
          {!isFixedFlow && (
            <PropertyGroup>
              {curveSource ? (
                <ReadOnlyHint>{curveSource}. The solver finds the operating point where the curve meets the circuit's pressure drop.</ReadOnlyHint>
              ) : (
                <ReadOnlyHint style={{ color: '#c5221f' }}>No curve yet: choose a pump model or define a custom curve.</ReadOnlyHint>
              )}
              {!isModelBased && !readOnlyProp && (
                <ModelSelectButton onClick={() => setShowPicker(true)} style={{ marginTop: 6 }}>
                  <span>{hasCustomCurve ? 'Edit custom curve…' : 'Define curve / choose model…'}</span>
                  <Icon path={mdiChevronDown} size={0.5} color="#5f6368" />
                </ModelSelectButton>
              )}
            </PropertyGroup>
          )}
          {isFixedFlow && (
            <PropertyGroup>
              <PropertyLabel>Fixed Flow Rate [{units.unit('flow')}]</PropertyLabel>
              <PropertyInput
                type="number"
                step="any"
                disabled={readOnlyProp}
                value={parseFloat(convertForDisplay(properties?.fixed_flow_lpm ?? DEFAULT_FIXED_FLOW_LPM, 'flow', units.system).toFixed(4))}
                onChange={(e) => {
                  const raw = parseFloat(e.target.value) || 0;
                  const stored = convertToMetric(raw, 'flow', units.system);
                  handleFixedFlowValueChange(stored);
                }}
              />
              <ReadOnlyHint>Shown on the pump tile. The solver uses this flow and reports the pump head the circuit needs.</ReadOnlyHint>
            </PropertyGroup>
          )}
        </CollapsibleSection>
      )}

      {canAddToLibrary && !readOnlyProp && component.component_type !== 'cooling_package'
        && (!isModelBased || libraryMessage?.ok) && (
        <CollapsibleSection title="Component Library">
          {libraryMessage && (
            <ReadOnlyHint style={{ color: libraryMessage.ok ? '#188038' : '#c5221f' }} role="status">
              {libraryMessage.text}
            </ReadOnlyHint>
          )}
          {!isModelBased && !libraryForm && (
            <>
              <ReadOnlyHint>
                Make this component available to everyone in the model picker.
                {isPump && isFixedFlow && ' A library pump needs its curve: switch to "Pump curve" and define one first.'}
              </ReadOnlyHint>
              <ModelSelectButton
                style={{ marginTop: 6 }}
                disabled={isPump && isFixedFlow}
                onClick={() => {
                  setLibraryMessage(null);
                  setLibraryForm({ name: custom_name || component.name, manufacturer: '', description: '' });
                }}
              >
                <span>Add to component library…</span>
              </ModelSelectButton>
            </>
          )}
          {!isModelBased && libraryForm && (
            <>
              <PropertyGroup>
                <PropertyLabel htmlFor="library-name">Library name</PropertyLabel>
                <PropertyInput
                  id="library-name"
                  value={libraryForm.name}
                  onChange={(e) => setLibraryForm({ ...libraryForm, name: e.target.value })}
                />
              </PropertyGroup>
              <PropertyGroup>
                <PropertyLabel htmlFor="library-manufacturer">Manufacturer</PropertyLabel>
                <PropertyInput
                  id="library-manufacturer"
                  value={libraryForm.manufacturer}
                  placeholder="optional"
                  onChange={(e) => setLibraryForm({ ...libraryForm, manufacturer: e.target.value })}
                />
              </PropertyGroup>
              <PropertyGroup>
                <PropertyLabel htmlFor="library-description">Description</PropertyLabel>
                <PropertyInput
                  id="library-description"
                  value={libraryForm.description}
                  placeholder="optional"
                  onChange={(e) => setLibraryForm({ ...libraryForm, description: e.target.value })}
                />
              </PropertyGroup>
              <ReadOnlyHint>
                Saved: {Array.isArray(properties?.custom_curve) ? 'the curve, ' : ''}
                the parameters, ports and temperature limits. The coolant and a fixed flow stay with this canvas.
              </ReadOnlyHint>
              <ToggleRow style={{ marginTop: 8, gap: 6 }}>
                <ModelSelectButton
                  style={{ justifyContent: 'center', background: '#1a73e8', color: '#fff', borderColor: '#1a73e8' }}
                  disabled={libraryBusy || !libraryForm.name.trim()}
                  onClick={async () => {
                    if (!onAddToLibrary) return;
                    setLibraryBusy(true);
                    setLibraryMessage(null);
                    try {
                      await onAddToLibrary(node.id, {
                        name: libraryForm.name.trim(),
                        manufacturer: libraryForm.manufacturer?.trim(),
                        description: libraryForm.description?.trim(),
                      });
                      setLibraryMessage({
                        ok: true,
                        text: `Added to the library as “${libraryForm.name.trim()}”. This component now uses the library model.`,
                      });
                      setLibraryForm(null);
                    } catch (e: any) {
                      setLibraryMessage({ ok: false, text: e.message || 'Could not add the component.' });
                    } finally {
                      setLibraryBusy(false);
                    }
                  }}
                >
                  {libraryBusy ? 'Adding…' : 'Add to library'}
                </ModelSelectButton>
                <ModelSelectButton
                  style={{ justifyContent: 'center' }}
                  disabled={libraryBusy}
                  onClick={() => { setLibraryForm(null); setLibraryMessage(null); }}
                >
                  Cancel
                </ModelSelectButton>
              </ToggleRow>
            </>
          )}
        </CollapsibleSection>
      )}

      <CollapsibleSection title="Temperature Limits">
        <PropertyGroup>
          <PropertyLabel>Max Inlet Temperature [{units.unit('temperature')}]</PropertyLabel>
          <PropertyInput
            type="number"
            value={max_temperatures?.inlet != null
              ? parseFloat(units.display(max_temperatures.inlet, 'temperature'))
              : ''}
            placeholder="No limit"
            disabled={readOnlyProp}
            onChange={(e) => {
              if (e.target.value === '') {
                handleMaxTempChange('inlet', '');
              } else {
                const raw = parseFloat(e.target.value);
                const metric = units.toMetric(raw, 'temperature');
                handleMaxTempChange('inlet', String(metric));
              }
            }}
          />
        </PropertyGroup>
        <PropertyGroup>
          <PropertyLabel>Max Outlet Temperature [{units.unit('temperature')}]</PropertyLabel>
          <PropertyInput
            type="number"
            value={max_temperatures?.outlet != null
              ? parseFloat(units.display(max_temperatures.outlet, 'temperature'))
              : ''}
            placeholder="No limit"
            disabled={readOnlyProp}
            onChange={(e) => {
              if (e.target.value === '') {
                handleMaxTempChange('outlet', '');
              } else {
                const raw = parseFloat(e.target.value);
                const metric = units.toMetric(raw, 'temperature');
                handleMaxTempChange('outlet', String(metric));
              }
            }}
          />
        </PropertyGroup>
      </CollapsibleSection>

      {(component.component_type === 'branch' || component.component_type === 'merge') && (
        <CollapsibleSection title="Ports" defaultExpanded>
          {component.component_type === 'branch' ? (
            <>
              <PropertyGroup>
                <PropertyLabel>Input Ports</PropertyLabel>
                <PropertyValue>1</PropertyValue>
              </PropertyGroup>
              <PropertyGroup>
                <PropertyLabel>Output Ports</PropertyLabel>
                <PropertySelect
                  value={component.output_ports}
                  disabled={readOnlyProp}
                  onChange={(e) => {
                    const newPorts = parseInt(e.target.value, 10);
                    onNodeUpdate(node.id, {
                      component: { ...component, output_ports: newPorts },
                    });
                  }}
                >
                  {[2, 3, 4, 5, 6, 7, 8].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </PropertySelect>
              </PropertyGroup>
            </>
          ) : (
            <>
              <PropertyGroup>
                <PropertyLabel>Input Ports</PropertyLabel>
                <PropertySelect
                  value={component.input_ports}
                  disabled={readOnlyProp}
                  onChange={(e) => {
                    const newPorts = parseInt(e.target.value, 10);
                    onNodeUpdate(node.id, {
                      component: { ...component, input_ports: newPorts },
                    });
                  }}
                >
                  {[2, 3, 4, 5, 6, 7, 8].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </PropertySelect>
              </PropertyGroup>
              <PropertyGroup>
                <PropertyLabel>Output Ports</PropertyLabel>
                <PropertyValue>1</PropertyValue>
              </PropertyGroup>
            </>
          )}
        </CollapsibleSection>
      )}

      {showPicker && (
        <ComponentPickerModal
          componentType={component.component_type}
          currentModelId={component.id}
          currentComponent={component}
          currentProperties={properties}
          availableModels={availableModels}
          onSelect={handleModelSelect}
          onClose={() => setShowPicker(false)}
        />
      )}
    </PanelContainer>
  );
};
